// The camera's aspect must match the viewport, and when it does not the failure
// is SILENT AND TOTAL.
//
// `aspect: 0` collapses the projection matrix: every vertex maps into a
// degenerate frustum, nothing rasterises, and the canvas is black. There is no
// warning and no error — three does not validate it, and a frustum built from
// that matrix still reports every object as "inside", so even a culling check
// says the scene is fine. That combination cost a long hunt: the renderer was
// healthy (an unlit probe box rendered 2916 pixels through it), the scene had 11
// visible meshes, the materials had all compiled, the console was clean, and the
// viewport was black.
//
// WHY IT HAPPENS HERE: R3F derives the camera's aspect from its measured size.
// With `gl` supplied as an ASYNC factory — which `WebGPURenderer` requires, since
// it is unusable until `init()` resolves — the first frames can run before that
// measurement reaches the camera, and nothing re-runs it if the size never
// changes afterwards. The WebGL path never shows it because R3F builds that
// renderer synchronously.
//
// So this is a GUARD, not a workaround for one renderer: it asserts the
// invariant on every frame it could be violated, fixes it, and says so ONCE.
// Silently repairing it would hide a real regression in the size plumbing.

import { useEffect } from 'react'
import { useThree } from '@react-three/fiber'

export default function CameraAspectGuard() {
  const camera = useThree((s) => s.camera)
  const size = useThree((s) => s.size)

  useEffect(() => {
    if (!camera?.isPerspectiveCamera) return
    const { width, height } = size
    if (!width || !height) return
    const want = width / height
    // Exact-enough: this is about 0 and about a stale value after a resize, not
    // about float noise.
    if (Math.abs((camera.aspect || 0) - want) < 1e-6) return

    const was = camera.aspect
    camera.aspect = want
    camera.updateProjectionMatrix()
    if (!was) {
      // ONCE, and only for the case that renders nothing at all. A stale aspect
      // after a resize is ordinary; an aspect of 0 means the camera never got a
      // size, and that is worth a line because the symptom is a black viewport
      // with a clean console.
      console.warn('[camera] aspect was 0 — the projection was degenerate and nothing could '
        + `rasterise; set to ${want.toFixed(4)} from the ${width}x${height} viewport`)
    }
  }, [camera, size])

  return null
}
